import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import Edit2FillIcon from "remixicon-react/Edit2FillIcon";
import CommonPagination from "../CommonPagination/CommonPagination";

// Define the type for promo code data
interface PromoCode {
    promoId: number;
    filterKey:string;
    filterValue:string;
    promoCode: string;
    promoDescription: string;
    isVisible: boolean;
    totalCoupons: number;
    couponsRemaining: number;
    maxCouponsPerCustomer: number;
    activationTime: number;
    expiryTime: number;
    onlyForNewUser: boolean | null;
    device: string | null;
    minCartValue: number;
    suggestedProducts: string | null;
    promoAmount: number | null;
    exclusive: boolean | null;
    enabled: boolean;
}

interface PromoCodeListDataProps {
    activeByDate?: string;
    expiredByDate?: string;
    clearTrigger?:any;
    filterKey?:any;
    filterValue?:any;
    filterPromoCode?:any;
    shouldDownloadDump?:any,
    onDumpHandled?:any
}

const TABLE_HEADERS = [
    "Promo Code", "Promo Type", "Category", "Description", "Device", "Total",
    "Remaining", "Max/User", "Activation", "Expiry", "Enabled", "Visible", "Action"
];

const YesNoPill = ({ value }: { value: boolean }) => (
    <span className={`inline-block whitespace-nowrap px-1.5 py-0.5 rounded text-[11px] font-medium border ${value ? "bg-green-100 text-green-800 border-green-200" : "bg-red-100 text-red-800 border-red-200"}`}>
        {value ? "Yes" : "No"}
    </span>
);

function formatDate(ts: number) {
    if (!ts) return "—";
    const d = new Date(ts);
    if (isNaN(d.getTime())) return "—";
    return d.toISOString().slice(0, 10);
}

const PromoCodeListData = ({ activeByDate = "", expiredByDate = "",filterKey='',filterValue='', filterPromoCode = "",shouldDownloadDump,onDumpHandled}: PromoCodeListDataProps) => {
    const [data, setData] = useState<PromoCode[]>([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("");
    const [apiResponse, setApiResponse] = useState<any>()
    const [currentPage, setCurrentPage] = useState(1);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true)
            setError("")
            try {
                const params = new URLSearchParams({
                    start: ((currentPage - 1)).toString(),  // 👈 Add pagination offset
                    length: "10",
                    promoSearch: filterPromoCode,
                    activeByDate: activeByDate || "",
                    expiredByDate: expiredByDate || "",
                    filterKey: filterKey ||'',
                    filterValue:filterValue || ''
                })
                // Add dumpUrl param if user clicked the button
                if (shouldDownloadDump) {
                    params.append("dumpUrl", "true")
                }
                const res = await fetch(`${process.env.REACT_APP_CMS_SERVICE}/cmsApi/v1/promocode/list?${params}`)
                if (!res.ok) throw new Error("Failed to fetch promo codes");
                const json = await res.json();
                setApiResponse(json)
                setData(json?.data?.promoAttributesList || [])
                if (shouldDownloadDump && json?.data?.dumpFileUrl) {
                    window.open(json.data.dumpFileUrl, "_blank") // or trigger download
                }
            } catch (err) {
                if (err instanceof Error) setError(err.message)
                else setError("Unknown error")
            } finally {
                setLoading(false)
                if (shouldDownloadDump) {
                    onDumpHandled()
                }
            }
        }
        fetchData()
    }, [activeByDate, expiredByDate,filterKey ,filterValue ,currentPage,filterPromoCode,shouldDownloadDump]) // 👈 Add currentPage dependency

    // The search is applied server side via `promoSearch`; re-filtering the
    // response by promo code as well would drop rows the API matched on their
    // description.

    return (
        <>
            <div className="mt-2.5 campaign-list bg-white mb-[54px]">
                <table className="w-full">
                    <thead className="bg-[#343434]">
                        <tr>
                            {TABLE_HEADERS.map((header) => (
                                <th key={header} className="px-4 py-3 text-left text-xs font-semibold text-white whitespace-nowrap">{header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={TABLE_HEADERS.length} className="px-4 py-12 text-center text-xs text-gray-500">Loading promo codes...</td>
                            </tr>
                        )}
                        {!loading && error && (
                            <tr>
                                <td colSpan={TABLE_HEADERS.length} className="px-4 py-12 text-center text-xs text-red-500">{error}</td>
                            </tr>
                        )}
                        {!loading && !error && data.length === 0 && (
                            <tr>
                                <td colSpan={TABLE_HEADERS.length} className="px-4 py-12 text-center text-xs text-gray-500">No promo codes found.</td>
                            </tr>
                        )}
                        {!loading && !error && data.map((item) => (
                            <tr key={item.promoId} className='[&:nth-child(even)]:bg-[#f7f7f7]'>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] font-medium">{item.promoCode}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">{item.filterKey || "—"}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">{item.filterValue || "—"}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">{item.promoDescription || "—"}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">{item.device || "—"}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">{item.totalCoupons}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">{item.couponsRemaining}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]">{item.maxCouponsPerCustomer}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] whitespace-nowrap">{formatDate(item.activationTime)}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F] whitespace-nowrap">{formatDate(item.expiryTime)}</td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]"><YesNoPill value={item.enabled} /></td>
                                <td className="px-4 py-3 text-left text-xs text-[#3F3F3F]"><YesNoPill value={item.isVisible} /></td>
                                <td className="px-4 py-3">
                                    <button
                                        onClick={() => navigate(`/promocode-list/update/${item.promoCode}`)}
                                        className="text-blue-600 hover:text-blue-800 transition-colors"
                                        title="View / Update"
                                    >
                                        <Edit2FillIcon size={16} />
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {apiResponse && data.length > 0 && (
                <CommonPagination
                    data={{
                        ...apiResponse.data,
                        currentPage: apiResponse.data.page,
                        pageSize: apiResponse.data.size,
                        totalCount: apiResponse.data.count
                    }}
                    PAGE_SIZE={10}
                    onPageChange={(page:any) => setCurrentPage(page)} // 👈 This triggers the API call
                />
            )}
        </>
    )
}
export default PromoCodeListData
